import React, { useContext, useMemo, useState, useEffect } from 'react';
import { UserContext, ThemeContext } from '../Context';
import axios from 'axios';
import {
  Box,
  Paper,
  CardContent,
  Typography,
  Grid,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Description,
  ListAlt,
  ShortText,
  Whatshot,
  EmojiEvents,
} from '@mui/icons-material';

const Statistics = () => {
  const { user } = useContext(UserContext);
  const { theme } = useContext(ThemeContext);

  const isDark = theme === 'dark';
  const currentYear = new Date().getFullYear();
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMetric, setSelectedMetric] = useState('documents');
  
  // États pour les données de l'API
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingYears, setLoadingYears] = useState(true);
  const [error, setError] = useState(null);
  const [statisticsEnabled, setStatisticsEnabled] = useState(true);
  const [checkingStatus, setCheckingStatus] = useState(true);
  
  // État pour le streak
  const [streakData, setStreakData] = useState({
    currentStreak: 0,
    bestStreak: 0,
    bestStreakStart: null,
    bestStreakEnd: null
  });
  const [loadingStreak, setLoadingStreak] = useState(true);

  const getIsoDay = (date) => (date.getDay() + 6) % 7; // Lundi = 0 ... Dimanche = 6

  // Formater une date en YYYY-MM-DD en heure locale (pas UTC)
  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Vérifier si les statistiques sont activées
  useEffect(() => {
    const checkStatus = async () => {
      try {
        const response = await axios.get('/api/stats/status');
        setStatisticsEnabled(response.data.enabled);
      } catch (err) {
        console.error('Erreur lors de la vérification du statut:', err);
        setStatisticsEnabled(true); // Par défaut activé en cas d'erreur
      } finally {
        setCheckingStatus(false);
      }
    };

    checkStatus();
  }, []);

  // Charger les années disponibles
  useEffect(() => {
    const fetchYears = async () => {
      if (!statisticsEnabled) return;
      
      setLoadingYears(true);
      try {
        const token = sessionStorage.getItem('authToken');
        if (!token) return;

        const response = await axios.get('/api/stats/years', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const years = response.data.years || [currentYear];
        setAvailableYears(years);
        
        // Sélectionner la première année (la plus récente) si l'année courante n'est pas dans la liste
        if (years.length > 0 && !years.includes(selectedYear)) {
          setSelectedYear(years[0]);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des années:', err);
        // En cas d'erreur, garder l'année courante
        setAvailableYears([currentYear]);
      } finally {
        setLoadingYears(false);
      }
    };

    if (user && !checkingStatus && statisticsEnabled) {
      fetchYears();
    }
  }, [user, currentYear, checkingStatus, statisticsEnabled]);

  // Charger les données de streak
  useEffect(() => {
    const fetchStreak = async () => {
      if (!statisticsEnabled) return;
      
      setLoadingStreak(true);
      try {
        const token = sessionStorage.getItem('authToken');
        if (!token) return;

        const response = await axios.get('/api/stats/streak', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setStreakData(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement du streak:', err);
      } finally {
        setLoadingStreak(false);
      }
    };

    if (user && !checkingStatus && statisticsEnabled) {
      fetchStreak();
    }
  }, [user, checkingStatus, statisticsEnabled]);

  // Charger les statistiques depuis l'API
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const token = sessionStorage.getItem('authToken');
        if (!token) {
          setError('Non authentifié');
          setLoading(false);
          return;
        }

        const response = await axios.get(`/api/stats/${selectedYear}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        setStatsData(response.data);
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError('Impossible de charger les statistiques');
      } finally {
        setLoading(false);
      }
    };

    if (user && !loadingYears && statisticsEnabled) {
      fetchStats();
    }
  }, [selectedYear, user, loadingYears, statisticsEnabled]);

  // Générer les données du heatmap à partir des données de l'API
  const heatmapData = useMemo(() => {
    const start = new Date(selectedYear, 0, 1);
    const end = new Date(selectedYear + 1, 0, 1);
    const days = [];
    let cursor = new Date(start);
    
    while (cursor < end) {
      const dateKey = formatLocalDate(cursor); // Utiliser l'heure locale
      const dailyStats = statsData?.dailyData?.[dateKey];
      
      // Calculer la valeur en fonction de la métrique sélectionnée
      let value = 0;
      if (dailyStats) {
        switch (selectedMetric) {
          case 'documents':
            value = dailyStats.documents || 0;
            break;
          case 'rubriques':
            value = dailyStats.rubriques || 0;
            break;
          case 'mots':
            // Pour les mots, on normalise (diviser par 100 pour avoir une échelle raisonnable)
            value = Math.min(6, Math.ceil((dailyStats.mots || 0) / 100));
            break;
          default:
            value = 0;
        }
      }
      
      days.push({
        date: new Date(cursor),
        value,
        details: dailyStats || { documents: 0, rubriques: 0, mots: 0 }
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }, [selectedYear, statsData, selectedMetric]);

  const monthNames = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const weekdayLabels = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

  const weeks = useMemo(() => {
    if (!heatmapData.length) return [];
    const startDay = getIsoDay(heatmapData[0].date);
    const rows = [];
    let currentWeek = Array.from({ length: startDay }, () => null);
    heatmapData.forEach((day) => {
      currentWeek.push(day);
      if (currentWeek.length === 7) {
        rows.push(currentWeek);
        currentWeek = [];
      }
    });
    if (currentWeek.length) {
      while (currentWeek.length < 7) currentWeek.push(null);
      rows.push(currentWeek);
    }
    return rows;
  }, [heatmapData]);

  const monthLabels = useMemo(() => {
    const labels = [];
    const seen = new Set();
    weeks.forEach((week, idx) => {
      const firstValid = week.find(Boolean);
      if (!firstValid) return;
      const month = firstValid.date.getMonth();
      if (!seen.has(month)) {
        labels.push({ month, weekIdx: idx });
        seen.add(month);
      }
    });
    return labels;
  }, [weeks]);

  const getColor = (value) => {
    const palette = isDark
      ? ['#1b2637', '#113f67', '#1c6ca1', '#5fa8d3', '#b9e6ff']
      : ['#ebedf0', '#c6e48b', '#7bc96f', '#239a3b', '#196127'];
    if (value === 0 || value === undefined || value === null) return palette[0];
    if (value <= 1) return palette[1];
    if (value <= 2) return palette[2];
    if (value <= 4) return palette[3];
    return palette[4];
  };

  // Données par jour de la semaine depuis l'API (filtrées par métrique)
  const weekdayCounts = useMemo(() => {
    if (!statsData?.weekdayData) return Array(7).fill(0);
    switch (selectedMetric) {
      case 'documents':
        return statsData.weekdayData.documents || Array(7).fill(0);
      case 'rubriques':
        return statsData.weekdayData.rubriques || Array(7).fill(0);
      case 'mots':
        return statsData.weekdayData.mots || Array(7).fill(0);
      default:
        return Array(7).fill(0);
    }
  }, [statsData, selectedMetric]);

  // Données par heure depuis l'API (filtrées par métrique)
  const hourlyCounts = useMemo(() => {
    if (!statsData?.hourlyData) return Array(24).fill(0);
    switch (selectedMetric) {
      case 'documents':
        return statsData.hourlyData.documents || Array(24).fill(0);
      case 'rubriques':
        return statsData.hourlyData.rubriques || Array(24).fill(0);
      case 'mots':
        return statsData.hourlyData.mots || Array(24).fill(0);
      default:
        return Array(24).fill(0);
    }
  }, [statsData, selectedMetric]);

  // Totaux depuis l'API
  const totals = useMemo(() => {
    return statsData?.totals || { documents: 0, rubriques: 0, mots: 0 };
  }, [statsData]);

  // Formater les nombres avec séparateur de milliers
  const formatNumber = (num) => {
    return num.toLocaleString('fr-FR');
  };

  // Formater une date YYYY-MM-DD en format lisible
  const formatStreakDate = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const placeholders = [
    {
      key: 'documents',
      title: 'Documents modifiés',
      value: totals.documents,
      icon: Description,
    },
    {
      key: 'rubriques',
      title: 'Rubriques générées',
      value: totals.rubriques,
      icon: ListAlt,
    },
    {
      key: 'mots',
      title: 'Mots générés',
      value: totals.mots,
      icon: ShortText,
    },
  ];

  if (!user) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="warning">
          Veuillez vous connecter pour consulter vos statistiques.
        </Alert>
      </Box>
    );
  }

  if (checkingStatus) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
        }}
      >
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!statisticsEnabled) {
    return (
      <Box
        component={Paper}
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          bgcolor: isDark ? '#233044' : '#ffffff',
          color: isDark ? '#fff' : 'inherit',
        }}
      >
        <Alert severity="info">
          Les statistiques ne sont pas activées par votre zone.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      component={Paper}
      elevation={2}
      sx={{
        p: 1,
        borderRadius: 2,
        bgcolor: isDark ? '#233044' : '#ffffff',
        color: isDark ? '#fff' : 'inherit',
        boxShadow: isDark 
          ? '0 4px 20px rgba(0, 0, 0, 0.5)'
          : '0 4px 20px rgba(0,0,0,0.05)',
      }}
    >
      <CardContent sx={{ p: 0 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
          mb={0.25}
        >
          <Typography
            variant="h6"
            component="h2"
            sx={{ color: isDark ? '#90caf9' : 'inherit' }}
          >
            Statistiques
          </Typography>

          <FormControl
            size="small"
            sx={{ minWidth: 160 }}
            variant="outlined"
          >
            <InputLabel id="statistics-year-label">Année</InputLabel>
            <Select
              labelId="statistics-year-label"
              label="Année"
              value={selectedYear}
              onChange={(event) => setSelectedYear(event.target.value)}
              sx={{
                '& .MuiSelect-select': { py: 1.25, fontSize: '1.25rem' },
              }}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year} sx={{ fontSize: '1rem' }}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: isDark ? '#b0bec5' : 'inherit', mb: 0.5 }}
        >
          Retrouvez vos statistiques personnelles, celles-ci ne sont consultables que par vous.
        </Typography>

        <Divider
          sx={{
            mt: 1,
            mb: 2,
            borderColor: isDark ? 'rgba(144, 202, 249, 0.2)' : 'inherit',
          }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress size={40} />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        ) : (
          <>
            {/* Section Streak */}
            <Grid container spacing={2} sx={{ mt: 0, mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(255, 152, 0, 0.4)' : 'rgba(255, 152, 0, 0.3)',
                    bgcolor: isDark ? 'rgba(255, 152, 0, 0.08)' : 'rgba(255, 152, 0, 0.05)',
                    p: 1.5,
                    height: '100%',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <Whatshot sx={{ color: '#ff9800', mr: 1, fontSize: 28 }} />
                    <Typography variant="subtitle1" sx={{ color: isDark ? '#fff' : 'inherit', fontWeight: 600 }}>
                      Streak actuel
                    </Typography>
                  </Box>
                  {loadingStreak ? (
                    <CircularProgress size={24} sx={{ color: '#ff9800' }} />
                  ) : (
                    <Box display="flex" alignItems="baseline" gap={1}>
                      <Typography
                        variant="h3"
                        sx={{
                          color: '#ff9800',
                          fontWeight: 700,
                        }}
                      >
                        {streakData.currentStreak}
                      </Typography>
                      <Typography variant="body1" sx={{ color: isDark ? '#b0bec5' : '#666' }}>
                        jour{streakData.currentStreak > 1 ? 's' : ''} consécutif{streakData.currentStreak > 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  )}
                  <Typography variant="caption" sx={{ color: isDark ? '#b0bec5' : '#888', mt: 1, display: 'block' }}>
                    Nombre de jours d'affilée avec au moins une modification
                  </Typography>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(255, 215, 0, 0.4)' : 'rgba(255, 193, 7, 0.4)',
                    bgcolor: isDark ? 'rgba(255, 215, 0, 0.08)' : 'rgba(255, 193, 7, 0.05)',
                    p: 1.5,
                    height: '100%',
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    <EmojiEvents sx={{ color: '#ffc107', mr: 1, fontSize: 28 }} />
                    <Typography variant="subtitle1" sx={{ color: isDark ? '#fff' : 'inherit', fontWeight: 600 }}>
                      Record de streak
                    </Typography>
                  </Box>
                  {loadingStreak ? (
                    <CircularProgress size={24} sx={{ color: '#ffc107' }} />
                  ) : (
                    <>
                      <Box display="flex" alignItems="baseline" gap={1}>
                        <Typography
                          variant="h3"
                          sx={{
                            color: '#ffc107',
                            fontWeight: 700,
                          }}
                        >
                          {streakData.bestStreak}
                        </Typography>
                        <Typography variant="body1" sx={{ color: isDark ? '#b0bec5' : '#666' }}>
                          jour{streakData.bestStreak > 1 ? 's' : ''}
                        </Typography>
                      </Box>
                      {streakData.bestStreakStart && streakData.bestStreakEnd && (
                        <Typography variant="caption" sx={{ color: isDark ? '#b0bec5' : '#888', mt: 0.5, display: 'block' }}>
                          Du {formatStreakDate(streakData.bestStreakStart)} au {formatStreakDate(streakData.bestStreakEnd)}
                        </Typography>
                      )}
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              {placeholders.map((item) => {
                const Icon = item.icon;
                return (
                  <Grid item xs={12} md={4} key={item.key}>
                    <Paper
                      variant="outlined"
                      sx={{
                        height: '100%',
                        borderRadius: 2,
                        borderColor:
                          selectedMetric === item.key
                            ? isDark
                              ? '#90caf9'
                              : '#1976d2'
                            : isDark
                            ? 'rgba(144, 202, 249, 0.3)'
                            : 'rgba(0,0,0,0.08)',
                        bgcolor:
                          selectedMetric === item.key
                            ? isDark
                              ? 'rgba(144, 202, 249, 0.08)'
                              : 'rgba(25, 118, 210, 0.08)'
                            : isDark
                            ? 'rgba(255, 255, 255, 0.04)'
                            : '#fafafa',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                      }}
                      onClick={() => setSelectedMetric(item.key)}
                      role="button"
                      aria-pressed={selectedMetric === item.key}
                    >
                      <CardContent sx={{ p: 1 }}>
                        <Box display="flex" alignItems="center" mb={0.75}>
                          <Icon
                            sx={{
                              color: isDark ? '#90caf9' : '#1976d2',
                              mr: 1,
                            }}
                          />
                          <Typography
                            variant="subtitle2"
                            sx={{
                              color: isDark ? '#fff' : 'inherit',
                              fontWeight: selectedMetric === item.key ? 600 : 500,
                            }}
                          >
                            {item.title}
                          </Typography>
                        </Box>
                        <Typography
                          variant="h4"
                          sx={{ 
                            color: isDark ? '#90caf9' : '#1976d2',
                            fontWeight: 600,
                          }}
                        >
                          {formatNumber(item.value)}
                        </Typography>
                      </CardContent>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'rgba(0,0,0,0.08)',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
                    p: 1.25,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.25, color: isDark ? '#fff' : 'inherit' }}>
                    Activité quotidienne {selectedYear} - {placeholders.find(p => p.key === selectedMetric)?.title}
                  </Typography>
                  <Box
                    sx={{
                      overflowX: 'auto',
                      pb: 0.5,
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: `48px repeat(${weeks.length || 1}, 8px)`,
                        gridTemplateRows: `10px repeat(7, 8px)`,
                        columnGap: 1,
                        rowGap: 1,
                        alignItems: 'center',
                        width: 'max-content',
                      }}
                    >
                      {monthLabels.map((label) => (
                        <Typography
                          key={`month-${label.month}`}
                          variant="caption"
                          sx={{
                            gridColumn: label.weekIdx + 2,
                            gridRow: 1,
                            color: isDark ? '#b0bec5' : '#546e7a',
                            fontSize: 8,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {monthNames[label.month]}
                        </Typography>
                      ))}

                      {weekdayLabels.map((dayName, idx) => (
                        <Typography
                          key={`weekday-${dayName}`}
                          variant="caption"
                          sx={{
                            gridColumn: 1,
                            gridRow: idx + 2,
                            color: isDark ? '#b0bec5' : '#546e7a',
                            fontSize: 8,
                          }}
                        >
                          {dayName}
                        </Typography>
                      ))}

                      {weeks.map((week, weekIdx) =>
                        week.map((day, dayIdx) => (
                          <Tooltip
                            key={`day-${weekIdx}-${dayIdx}`}
                            title={
                              day
                                ? `${day.date.toLocaleDateString('fr-FR', {
                                    year: 'numeric',
                                    month: 'short',
                                    day: 'numeric',
                                  })} • ${day.details.documents} doc, ${day.details.rubriques} rub, ${formatNumber(day.details.mots)} mots`
                                : ''
                            }
                            placement="top"
                            arrow
                            disableInteractive
                          >
                            <Box
                              sx={{
                                gridColumn: weekIdx + 2,
                                gridRow: dayIdx + 2,
                                width: 8,
                                height: 8,
                                borderRadius: '2px',
                                bgcolor: getColor(day?.value),
                                opacity: day ? 1 : 0.35,
                              }}
                            />
                          </Tooltip>
                        ))
                      )}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      mt: 0.5,
                      color: isDark ? '#b0bec5' : 'inherit',
                    }}
                  >
                    <Typography variant="caption">Moins</Typography>
                    {[0, 1, 2, 4, 6].map((value) => (
                      <Box
                        key={value}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '2px',
                          bgcolor: getColor(value),
                        }}
                      />
                    ))}
                    <Typography variant="caption">Plus</Typography>
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'rgba(0,0,0,0.08)',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
                    p: 1.25,
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.5, color: isDark ? '#fff' : 'inherit' }}>
                    {placeholders.find(p => p.key === selectedMetric)?.title} par jour
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      display: 'flex',
                      gap: 0.75,
                      flex: 1,
                      justifyContent: 'space-between',
                    }}
                  >
                    {weekdayCounts.map((val, idx) => {
                      const max = Math.max(...weekdayCounts, 1);
                      const h = Math.max(6, (val / max) * 60);
                      const unit = selectedMetric === 'mots' ? 'mots' : selectedMetric === 'rubriques' ? 'rubriques' : 'documents';
                      return (
                        <Box
                          key={`weekday-bar-${idx}`}
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-end',
                            height: 110,
                            textAlign: 'center',
                            flex: 1,
                            minWidth: 32,
                            maxWidth: 80,
                          }}
                        >
                          <Tooltip title={`${formatNumber(val)} ${unit}`} arrow>
                            <Box
                              sx={{
                                width: '70%',
                                height: h,
                                bgcolor: isDark ? '#5fa8d3' : '#239a3b',
                                borderRadius: '3px',
                                mb: 0.35,
                                transition: 'opacity 0.2s',
                                opacity: 0.9,
                              }}
                            />
                          </Tooltip>
                          <Typography variant="caption" sx={{ fontSize: 10, color: isDark ? '#b0bec5' : '#546e7a' }}>
                            {weekdayLabels[idx]}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper
                  variant="outlined"
                  sx={{
                    borderRadius: 2,
                    borderColor: isDark ? 'rgba(144, 202, 249, 0.3)' : 'rgba(0,0,0,0.08)',
                    bgcolor: isDark ? 'rgba(255, 255, 255, 0.02)' : '#fafafa',
                    p: 1.25,
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 0.5, color: isDark ? '#fff' : 'inherit' }}>
                    {placeholders.find(p => p.key === selectedMetric)?.title} par heure
                  </Typography>
                  <Box
                    sx={{
                      mt: 0.75,
                      display: 'flex',
                      gap: 0.35,
                      flex: 1,
                      justifyContent: 'center',
                    }}
                  >
                    {hourlyCounts.map((val, idx) => {
                      const max = Math.max(...hourlyCounts, 1);
                      const h = Math.max(6, (val / max) * 60);
                      const unit = selectedMetric === 'mots' ? 'mots' : selectedMetric === 'rubriques' ? 'rubriques' : 'documents';
                      return (
                        <Tooltip key={`hour-bar-${idx}`} title={`${formatNumber(val)} ${unit} à ${String(idx).padStart(2, '0')}h`} arrow>
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'flex-end',
                              height: 100,
                              textAlign: 'center',
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: h,
                                bgcolor: isDark ? '#5fa8d3' : '#239a3b',
                                borderRadius: '3px',
                                mb: 0.25,
                                transition: 'opacity 0.2s',
                                opacity: 0.9,
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: 9,
                                color: isDark ? '#b0bec5' : '#546e7a',
                                visibility: idx % 3 === 0 || idx === 23 ? 'visible' : 'hidden',
                                lineHeight: 1.2,
                              }}
                            >
                              {`${String(idx).padStart(2, '0')}h`}
                            </Typography>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </CardContent>
    </Box>
  );
};

export default Statistics;
